function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import classnames from "classnames";
import throttle from "lodash.throttle";
import DefaultProps from "./default-props";
import { StyledCarousel } from "./carousel.scss";
const GO_TO_SPEED_MILLISECONDS = 500;
const GO_TO_ANIMATION_THROTTLE_DELAY = 100;
const PAUSE_AFTER_SWIPE_DELAY = 5000;
const ConditionalWrapper = ({ condition, wrapper, children })=>condition ? wrapper(children) : children;
// React 18 fix: react-slick calls preventDefault() on touch events, but React 18 registers them as passive.
// We neutralize preventDefault to avoid "passive event listener" warnings.
const noop = ()=>{};
const suppressPassivePreventDefault = (e)=>{
    e.preventDefault = noop;
};
const Carousel = /*#__PURE__*/ forwardRef((props, externalRef)=>{
    const slider = useRef(null);
    const carouselWrapperRef = useRef(null);
    // React 18 fix: Load react-slick client-side only to avoid hydration mismatch
    // (react-slick generates viewport-dependent DOM that differs between server and client)
    const [SliderComponent, setSliderComponent] = useState(null);
    useEffect(()=>{
        import("react-slick").then((mod)=>setSliderComponent(()=>mod.default || mod));
    }, []);
    useEffect(()=>{
        var _carouselWrapperRef_current;
        const el = (_carouselWrapperRef_current = carouselWrapperRef.current) === null || _carouselWrapperRef_current === void 0 ? void 0 : _carouselWrapperRef_current.querySelector(".slick-list");
        if (!el) return;
        const events = [
            "touchstart",
            "touchmove",
            "touchend"
        ];
        events.forEach((evt)=>el.addEventListener(evt, suppressPassivePreventDefault, {
                capture: true
            }));
        return ()=>{
            events.forEach((evt)=>el.removeEventListener(evt, suppressPassivePreventDefault, {
                    capture: true
                }));
        };
    });
    useImperativeHandle(externalRef, ()=>({
            scrollToSlide (index) {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickGoTo(index);
            },
            pause () {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickPause();
            },
            play () {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickPlay();
            },
            next () {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickNext();
            },
            prev () {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickPrev();
            }
        }), []);
    const { items, selectedIndex, itemRenderer, onSwipe, onChange, itemScale, lazyLoad } = props;
    const { autoplay, autoplaySpeed, infinite, slidesToShow, easing, arrows, nextArrow, prevArrow, dots, className, responsive } = props;
    const { centerMode, centerPadding, defaultIndex, swipeToSlide, showFocusEffect, defaultImage, carouselClassName, onRefSet, focusAfterSwipe, disableSlideOnStartClick, speed, beforeChange, draggable, slidesToScroll = 1, focusOnSelect = true } = props;
    const defaultIndexNumber = items ? Math.min(parseInt(defaultIndex), items.length) : 0;
    const hasDefault = defaultImage && defaultImage.src;
    const onIndexChanged = useCallback((idx)=>{
        if (!slider.current) return;
        let newIndex;
        if (idx !== null && centerMode) {
            newIndex = idx >= defaultIndexNumber && hasDefault ? idx + 1 : idx;
        } else {
            newIndex = defaultIndexNumber;
        }
        slider.current.slickGoTo(newIndex);
    }, [
        centerMode,
        defaultIndexNumber,
        hasDefault
    ]);
    const throttledOnIndexChanged = useCallback(throttle(onIndexChanged, GO_TO_SPEED_MILLISECONDS + GO_TO_ANIMATION_THROTTLE_DELAY, {
        trailing: true
    }), [
        onIndexChanged
    ]);
    useEffect(()=>{
        throttledOnIndexChanged(selectedIndex);
    }, [
        selectedIndex,
        throttledOnIndexChanged
    ]);
    useEffect(()=>{
        if ((slider === null || slider === void 0 ? void 0 : slider.current) && onRefSet) {
            onRefSet(slider.current);
        }
    }, [
        SliderComponent,
        onRefSet
    ]);
    if (!items) return null;
    let slides = [
        ...items
    ];
    if (hasDefault) {
        slides = [
            ...items.slice(0, defaultIndexNumber),
            defaultImage,
            ...items.slice(defaultIndexNumber)
        ];
    }
    if (!SliderComponent) {
        return /*#__PURE__*/ _jsx(StyledCarousel, {
            className: classnames("carousel", carouselClassName)
        });
    }
    const handleSwipe = (direction)=>{
        var _slider_current;
        (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickPause();
        if (autoplay) {
            setTimeout(()=>{
                var _slider_current;
                return (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.slickPlay();
            }, PAUSE_AFTER_SWIPE_DELAY);
        }
        if (onSwipe) {
            onSwipe(direction);
        }
    };
    const handleChange = (current)=>{
        if (onChange) {
            onChange(current);
        }
        if (focusAfterSwipe) {
            var _slider_current_innerSlider_list, _slider_current_innerSlider, _slider_current;
            slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : (_slider_current_innerSlider = _slider_current.innerSlider) === null || _slider_current_innerSlider === void 0 ? void 0 : (_slider_current_innerSlider_list = _slider_current_innerSlider.list) === null || _slider_current_innerSlider_list === void 0 ? void 0 : _slider_current_innerSlider_list.click();
        }
    };
    const focusSlider = ()=>{
        var _slider_current_innerSlider, _slider_current;
        if (slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : (_slider_current_innerSlider = _slider_current.innerSlider) === null || _slider_current_innerSlider === void 0 ? void 0 : _slider_current_innerSlider.list) {
            slider.current.innerSlider.list.setAttribute("tabindex", 0);
            slider.current.innerSlider.list.focus();
        }
    };
    const settings = {
        className: classnames({
            center: centerMode,
            focus: showFocusEffect,
            [className]: className
        }),
        speed: speed || GO_TO_SPEED_MILLISECONDS,
        dots,
        focusOnSelect,
        onSwipe: handleSwipe,
        afterChange: handleChange,
        beforeChange,
        infinite,
        slidesToScroll,
        arrows,
        nextArrow,
        prevArrow,
        swipeToSlide,
        slidesToShow: Math.min(slidesToShow, slides.length),
        centerPadding,
        easing,
        centerMode,
        autoplay,
        autoplaySpeed: parseInt(autoplaySpeed),
        lazyLoad,
        draggable
    };
    return /*#__PURE__*/ _jsx(StyledCarousel, {
        ref: carouselWrapperRef,
        className: classnames("carousel", carouselClassName),
        onTouchStart: disableSlideOnStartClick ? null : focusSlider,
        children: /*#__PURE__*/ _jsx(SliderComponent, _object_spread_props(_object_spread({
            ref: slider
        }, settings), {
            initialSlide: defaultIndexNumber,
            responsive: responsive,
            children: slides.map((item, index)=>/*#__PURE__*/ _jsx("div", {
                    className: "carousel-item",
                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                        condition: itemScale != 1,
                        wrapper: (children)=>/*#__PURE__*/ _jsx("div", {
                                style: {
                                    transform: `scale(${itemScale})`
                                },
                                children: children
                            }),
                        children: itemRenderer(item, index)
                    })
                }, `slide_${index}`))
        }))
    });
});
Carousel.defaultProps = DefaultProps;
export default Carousel;
